// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Style objects for inventory overview components.
 * Centralizes all styling logic to replace inline styles.
 */
import { INVENTORY_OVERVIEW_COLORS } from '@/inventory/components/overview/constants';
import { SxProps, Theme } from '@mui/material';

// Main container styles
export const containerStyles = {
  main: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  scrollContainer: {
    width: '100%',
    overflowX: 'auto',
  } as SxProps<Theme>,

  gridContent: {
    width: '100%',
    minWidth: 'fit-content',
  } as SxProps<Theme>,
};

// Grid row styles
export const gridRowStyles = {
  headerRow: {
    display: 'grid',
    borderBottom: '2px solid',
    borderBottomColor: 'grey.300',
  } as SxProps<Theme>,

  subHeaderRow: {
    display: 'grid',
    borderBottom: '2px solid',
    borderBottomColor: 'grey.300',
    minHeight: '4.5rem',
  } as SxProps<Theme>,

  productRow: {
    display: 'grid',
    width: '100%',
    '&:hover': {
      backgroundColor: 'grey.50',
    },
    borderBottom: '1px solid',
    borderBottomColor: 'grey.200',
  } as SxProps<Theme>,

  totalRow: {
    display: 'grid',
    width: '100%',
    borderTop: '1px solid',
    borderTopColor: 'grey.300',
    backgroundColor: 'grey.100',
    fontWeight: 600,
  } as SxProps<Theme>,
};

// Cell styles
export const cellStyles = {
  stickyHeader: {
    backgroundColor: 'grey.50',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 2,
    position: 'sticky',
    left: 0,
    zIndex: 3,
  } as SxProps<Theme>,

  stickyHeaderProduct: {
    backgroundColor: 'grey.50',
    alignItems: 'center',
    display: 'flex',
    px: 2,
    position: 'sticky',
    left: 0,
    zIndex: 3,
  } as SxProps<Theme>,

  header: {
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    p: 0,
  } as SxProps<Theme>,

  subHeader: {
    backgroundColor: 'white',
    p: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as SxProps<Theme>,

  stickySubHeader: {
    backgroundColor: 'white',
    p: 1,
    position: 'sticky',
    left: 0,
    zIndex: 3,
  } as SxProps<Theme>,

  productName: {
    backgroundColor: 'grey.50',
    fontWeight: 500,
    width: '225px',
    position: 'sticky',
    left: 0,
    zIndex: 1,
    p: 2,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    minWidth: 0,
  } as SxProps<Theme>,

  totalLabel: {
    backgroundColor: 'grey.50',
    fontWeight: 700,
    width: '225px',
    position: 'sticky',
    left: 0,
    zIndex: 1,
    p: 2,
    display: 'flex',
    alignItems: 'center',
  } as SxProps<Theme>,
};

// Location cell styles
export const locationCellStyles = {
  inventory: {
    p: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: INVENTORY_OVERVIEW_COLORS.INVENTORY.LOCATION_BACKGROUND,
  } as SxProps<Theme>,

  nonInventory: {
    p: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor:
      INVENTORY_OVERVIEW_COLORS.NON_INVENTORY.LOCATION_BACKGROUND,
  } as SxProps<Theme>,

  inTransit: {
    p: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TOTAL_BACKGROUND,
  } as SxProps<Theme>,
};

const baseTotalCellStyle: SxProps<Theme> = {
  backgroundColor: 'grey.50',
  p: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
};

// Total cell styles
export const totalCellStyles = {
  inventory: {
    ...baseTotalCellStyle,
    backgroundColor: INVENTORY_OVERVIEW_COLORS.INVENTORY.TOTAL_BACKGROUND,
  } as SxProps<Theme>,

  nonInventory: {
    ...baseTotalCellStyle,
    backgroundColor: INVENTORY_OVERVIEW_COLORS.NON_INVENTORY.TOTAL_BACKGROUND,
  } as SxProps<Theme>,

  inTransit: {
    ...baseTotalCellStyle,
    backgroundColor: INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TOTAL_BACKGROUND,
  } as SxProps<Theme>,

  grand: {
    ...baseTotalCellStyle,
    backgroundColor: 'grey.100',
    position: 'sticky',
    right: 0,
    zIndex: 1,
    // Width is now 175px - swapped with Device items column
    width: '175px',
  } as SxProps<Theme>,
};

const baseHeaderStyle: SxProps<Theme> = {
  p: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Header background styles
export const headerBackgroundStyles = {
  inventoryTotal: {
    ...baseHeaderStyle,
    backgroundColor: INVENTORY_OVERVIEW_COLORS.INVENTORY.TOTAL_BACKGROUND,
  } as SxProps<Theme>,

  nonInventoryTotal: {
    ...baseHeaderStyle,
    backgroundColor: INVENTORY_OVERVIEW_COLORS.NON_INVENTORY.TOTAL_BACKGROUND,
  } as SxProps<Theme>,

  inTransit: {
    ...baseHeaderStyle,
  } as SxProps<Theme>,

  inTransitTotal: {
    ...baseHeaderStyle,
    backgroundColor: INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TOTAL_BACKGROUND,
  } as SxProps<Theme>,

  total: {
    ...baseHeaderStyle,
    backgroundColor: 'grey.200',
  } as SxProps<Theme>,
};

// Asset cell styles
export const assetCellStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
    py: 1,
    backgroundColor: 'transparent',
  } as SxProps<Theme>,

  statusIndicators: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    alignItems: 'center',
  } as SxProps<Theme>,
};

// Typography styles
export const typographyStyles = {
  productLink: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    fontSize: '0.875rem',
    display: 'block',
    '&:hover': {
      textDecoration: 'underline',
      textDecorationStyle: 'dashed',
    },
  } as SxProps<Theme>,
};
